import { useEffect, useRef, useState, type JSX } from 'react'
import { useNavigate } from 'react-router'
import {
  Phone,
  LogOut,
  Settings,
  ChevronDown,
  UserCircle,
  Wifi,
  WifiOff,
  Globe,
  Menu,
  BookOpen,
  Sparkles,
} from 'lucide-react'
import voz360Logo from '../../assets/voz360.png'
import { AUTO_TIMEZONE, TIMEZONE_OPTIONS, timezoneOffsetLabel } from '../../utils/timezone.js'
import { useTimezone } from '../../hooks/useTimezone.js'
import { useUserType } from '../../hooks/useUserType.js'
import { useAgentStore } from '../../store/agentStore.js'
import { useAuthStore } from '../../store/authStore.js'
import { getSocket } from '../../socket/client.js'
import { api } from '../../api/client.js'
import { loginPathFor } from '../../auth/loginPath.js'
import { NetworkQualityMeter } from '../../modules/network-quality/NetworkQualityMeter.js'
import CallbacksWidget from '../../modules/callbacks/CallbacksWidget.js'
import VoicemailBadge from '../voicemail/VoicemailBadge.js'
import ApprovalDropdownWidget from '../../modules/conversations/ApprovalDropdownWidget.js'
import NotificationBellWidget from '../../modules/conversations/NotificationBellWidget.js'
import BreakCodeDialog from './BreakCodeDialog.js'
import StatusDropdown from './StatusDropdown.js'
import AiAssistantDrawer from './AiAssistantDrawer.js'
import ToggleSwitch from './ToggleSwitch.js'
import { cn } from '../../utils/cn.js'
import type { AgentStatus } from '@kc3/types'
import type { CallState } from '../../modules/softphone/useSoftphone.js'

const CALLS_VIEW = 'calls:view'
const VOICEMAIL_VIEW_OWN = 'voicemail:view_own'

export interface SoftphoneProps {
  registered: boolean
  callState: CallState
  remoteCLI: string | null
  isMuted: boolean
  answer: () => void
  hangup: () => void
  hold: () => void
  unhold: () => void
  dial: (digits: string) => void
  mute: () => void
  unmute: () => void
  transfer: (target: string) => void
}

export interface AgentHeaderBarProps {
  softphone: SoftphoneProps
  /** Mirrors the sidebar's collapsed state so the header's brand cell stays
   *  the same width — its right border is the divider the sidebar edge
   *  continues down the page. */
  sidebarCollapsed?: boolean
  onToggleLeftSidebar?: () => void
  /**
   * Toggles the floating softphone panel. Receives the dialer button's rect so
   * the panel can open anchored directly beneath the phone icon (the button
   * isn't the last item in the header, so a fixed right-edge position would
   * float away from it).
   */
  onToggleRightSidebar?: (anchor?: DOMRect) => void
}

export default function AgentHeaderBar(props: AgentHeaderBarProps): JSX.Element {
  const storeStatus = useAgentStore((s) => s.status)
  const userStatus = useAgentStore((s) => s.userStatus)
  const userState = useAgentStore((s) => s.userState)
  const setUserState = useAgentStore((s) => s.setUserState)
  const clearSession = useAuthStore((s) => s.clearSession)
  const user = useAuthStore((s) => s.user)
  // 'pbx' vs 'callcenter'. ACD Ready/Idle, break codes and queue figures are
  // contact-centre concepts; a PBX extension user has no queue to be ready for.
  const { showAcdControls } = useUserType()
  const canViewCalls = user?.permissions?.includes(CALLS_VIEW) ?? false
  const canViewVoicemail = user?.permissions?.includes(VOICEMAIL_VIEW_OWN) ?? false
  const [optimisticStatus, setOptimisticStatus] = useState<AgentStatus | null>(null)
  const [breakDialogOpen, setBreakDialogOpen] = useState(false)
  const navigate = useNavigate()


  const incall = props.softphone.callState === 'in_call' || props.softphone.callState === 'ringing_in' || props.softphone.callState === 'ringing_out'
  const logout = useAgentStore((s) => s.logout)

  // Clear optimistic status when store catches up
  useEffect(() => {
    if (optimisticStatus && storeStatus === optimisticStatus) {
      setOptimisticStatus(null)
    }
  }, [storeStatus, optimisticStatus])

  const [profileOpen, setProfileOpen] = useState(false)
  const [aiOpen, setAiOpen] = useState(false)
  const profileRef = useRef<HTMLDivElement>(null)
  // Measured on click so the softphone panel opens under the phone icon.
  const dialerRef = useRef<HTMLButtonElement>(null)
  const [tzOpen, setTzOpen] = useState(false)
  const { timezone: resolvedTz, stored: storedTz, setTimezone } = useTimezone()
  function emitEvent(event: 'pause' | 'unpause' | 'logout'): void {
    getSocket().emit('agent:status', { event })
  }

  async function handleSignOut(): Promise<void> {
    // Record logout in session history before clearing
    logout(false) // normal logout

    try {
      await api.post('/auth/logout')
    } catch (err) {
      console.error('Logout API call failed:', err)
    }
    emitEvent('logout')
    clearSession()
    // Navigate to the login page BEFORE RequirePermission re-renders with
    // a null user and bounces to /403.
    navigate(loginPathFor(window.location.pathname))
  }

  // Click outside to close profile dropdown
  useEffect(() => {
    if (!profileOpen) return
    function onClick(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [profileOpen])

  // Login ID is the agent's identity in this product; fall back to the
  // profile name, then email, before the generic label.
  const userDisplay =
    user?.loginId || user?.fullName || user?.email || 'Agent'
  const userInitials =
    userDisplay === 'Agent' ? 'AG' : userDisplay.slice(0, 2).toUpperCase()

  return (
    <>
      <header className="flex h-16 shrink-0 items-center justify-between border-b border-slate-200 bg-white pr-5">
        {/* Brand cell — locked to the sidebar width (w-72 / w-[72px]) so its
            right border is the same vertical line as the sidebar's edge. */}
        <div
          className={`flex h-full shrink-0 items-center border-r border-slate-200/90 px-3 transition-all duration-300 ease-in-out ${
            props.sidebarCollapsed ? 'w-[72px] justify-center' : 'w-72 justify-between'
          }`}
        >
          {!props.sidebarCollapsed && (
            <img src={voz360Logo} alt="Voz360" className="h-8 shrink-0" />
          )}
          {/* Hamburger menu */}
          <button
            onClick={props.onToggleLeftSidebar}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[3px] text-slate-600 transition-all hover:bg-slate-100 hover:text-slate-900 active:scale-95"
            title="Toggle sidebar"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>

        {/* Workspace identity — sits past the divider, in the main header area */}
        <span className="hidden min-w-0 truncate pl-5 text-[14px] font-semibold text-slate-900 sm:block">
          {userDisplay === 'Agent' ? 'Agent Workspace' : userDisplay}
        </span>

        {/* No active-call badge here: the caller number and call timer
            live in the softphone panel, which is open during every call —
            repeating them in the header was noise (removed on request). */}

        {/* Right */}
        <div className="flex min-w-0 flex-1 items-center justify-end gap-2.5 pl-5">
          <NetworkQualityMeter compact={true} />

          {/* SIP - Only show if user has telephony enabled */}
          {user?.allowTelephony && (
            <div className="flex h-8 w-8 items-center justify-center rounded-[3px] bg-slate-50 text-slate-400" title={props.softphone.registered ? 'SIP Connected' : 'SIP Disconnected'}>
              {props.softphone.registered ? (
                <Wifi className="h-4 w-4 text-emerald-500" />
              ) : (
                <WifiOff className="h-4 w-4 text-slate-300" />
              )}
            </div>
          )}

          {canViewCalls && <CallbacksWidget dial={props.softphone.dial} />}
          {canViewVoicemail && <VoicemailBadge />}
          <ApprovalDropdownWidget />
          <NotificationBellWidget disabled={userStatus.key !== 'available'} />

          {/* AI Assistant — sits with the other header widgets rather than in
              the profile menu, since it is meant to be reachable mid-call. */}
          <button
            onClick={() => setAiOpen(true)}
            className="flex h-9 w-9 items-center justify-center rounded-[3px] text-slate-600 transition-all hover:bg-[#3366FF]/10 hover:text-[#3366FF] active:scale-95"
            title="AI Assistant"
            aria-label="Open AI Assistant"
          >
            <Sparkles className="h-[18px] w-[18px]" />
          </button>

          {/* Dialer — opens the softphone panel directly beneath this button.
              Only shown if user has telephony enabled. */}
          {user?.allowTelephony && (
            <button
              ref={dialerRef}
              onClick={() => props.onToggleRightSidebar?.(dialerRef.current?.getBoundingClientRect())}
              className="flex h-9 w-9 items-center justify-center rounded-[3px] bg-[#3366FF] text-white transition-all hover:bg-[#2952CC] hover:shadow-md active:scale-95"
              title="Dialer"
            >
              <Phone className="h-[18px] w-[18px]" />
            </button>
          )}

          {/* Extension Number */}
          {user?.allowTelephony && user?.extension && (
            <div className="flex items-center gap-2 rounded-[3px] border border-slate-200 bg-slate-50 px-3 py-2">
              <span className="text-[12px] font-medium text-slate-600">Ext.</span>
              <span className="text-[13px] font-semibold text-slate-900">{user.extension}</span>
            </div>
          )}

          {/* Ready/Idle toggle — telephony users only.
              Ready/Idle governs whether the dialer may route a call to this
              agent, so it means nothing to someone without telephony access:
              it was a permanently inert control implying a capability they do
              not have. Gated on the same `allowTelephony` flag as the SIP
              indicator above. */}
          {/* ...and gated on user_type as well.

              Ready/Idle is an ACD control: it decides whether the DIALER may
              route a queued call to this agent. A PBX extension user is never
              in a queue, so for them the switch was a permanently meaningless
              control implying a capability they do not have — the same defect
              as showing it to a user without telephony, one level up. */}
          {showAcdControls && (
            <div
              className={cn(
                'flex items-center gap-2 rounded-[3px] border border-slate-200 bg-white px-3 py-2',
                (incall || userStatus.key !== 'available') && 'opacity-60'
              )}
              title={userStatus.key !== 'available' ? 'Set status to Available first' : 'Toggle Ready/Idle State'}
            >
              <span className="text-[12px] font-medium text-slate-600">State</span>
              <ToggleSwitch
                checked={userState === 'Waiting'}
                onChange={(checked) => setUserState(checked ? 'Waiting' : 'Idle')}
                disabled={incall || userStatus.key !== 'available'}
                onLabel="Ready"
                offLabel="Idle"
                size="sm"
              />
            </div>
          )}

          {/* Status Dropdown — Available / break codes. FOR EVERY USER.

              STATUS and STATE are different things, and only one of them is
              contact-centre specific:

                STATUS (this) — "am I at my desk?" Presence and break
                  accounting. A PBX extension user takes lunch too, and their
                  break time is just as reportable. KC2 fetches `break_codes`
                  for every user at login with no cc_user gate.
                STATE (above)  — "may the ACD route a QUEUED call to me?"
                  In KC2 this is literally
                  `callcenter_config agent set state ... Waiting|Idle`, and it
                  is gated on cc_user. A PBX user is in no queue.

              Gating this dropdown on user_type would have taken break codes
              away from PBX users, which is a capability they have. */}
          <StatusDropdown disabled={incall} />

          {/* No break-timer button/dialog: while on break the BreakAssistant
              card (bottom-right) shows the timer and the only way back. */}
          {/* Profile */}
          <div className="relative ml-1" ref={profileRef}>
            {/* Icon, not an initials avatar. Styled like the other header
                buttons so the row reads as one set of controls; the avatar
                still appears inside the menu, where it sits beside the name
                and email and actually identifies the account. */}
            <button
              onClick={() => setProfileOpen((v) => !v)}
              className={`flex h-9 w-9 items-center justify-center rounded-[3px] transition-all active:scale-95 ${
                profileOpen
                  ? 'bg-[#3366FF]/10 text-[#3366FF]'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
              title={userDisplay}
              aria-label="Account menu"
              aria-haspopup="menu"
              aria-expanded={profileOpen}
            >
              <UserCircle className="h-[18px] w-[18px]" />
            </button>

            {profileOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 w-60 rounded-[3px] border border-slate-200 bg-white py-2 shadow-xl">
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-[3px] bg-[#3366FF] text-sm font-bold text-white">
                    {userInitials}
                  </div>
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-semibold text-slate-900">{userDisplay}</span>
                    {user?.extension && (
                      <span className="text-[11px] text-slate-400">Ext. {user.extension}</span>
                    )}
                  </div>
                </div>

                <div className="mx-3 my-1.5 border-t border-slate-100" />

                <button
                  onClick={() => { setProfileOpen(false); navigate('/agent/profile') }}
                  className="mx-1 flex w-[calc(100%-8px)] items-center gap-3 rounded-[3px] px-3 py-2.5 text-left text-[13px] text-slate-600 transition-colors hover:bg-slate-50"
                >
                  <UserCircle className="h-4 w-4 text-slate-400" />
                  Profile
                </button>

                <button
                  onClick={() => { setProfileOpen(false); navigate('/agent/devices') }}
                  className="mx-1 flex w-[calc(100%-8px)] items-center gap-3 rounded-[3px] px-3 py-2.5 text-left text-[13px] text-slate-600 transition-colors hover:bg-slate-50"
                >
                  <Settings className="h-4 w-4 text-slate-400" />
                  Settings
                </button>

                <button
                  onClick={() => { setProfileOpen(false); navigate('/agent/knowledge-base') }}
                  className="mx-1 flex w-[calc(100%-8px)] items-center gap-3 rounded-[3px] px-3 py-2.5 text-left text-[13px] text-slate-600 transition-colors hover:bg-slate-50"
                >
                  <BookOpen className="h-4 w-4 text-slate-400" />
                  Knowledge Base
                </button>

                <div className="relative mx-1">
                  <button
                    onClick={() => setTzOpen((v) => !v)}
                    className="flex w-full items-center gap-3 rounded-[3px] px-3 py-2.5 text-left text-[13px] text-slate-600 transition-colors hover:bg-slate-50"
                  >
                    <Globe className="h-4 w-4 text-slate-400" />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="text-[10px] text-slate-400">
                        Timezone · {timezoneOffsetLabel(resolvedTz)}
                      </span>
                      <span className="truncate text-[13px] font-medium text-slate-800">
                        {storedTz === AUTO_TIMEZONE ? resolvedTz : storedTz}
                      </span>
                    </div>
                    <ChevronDown className={cn('h-3 w-3 text-slate-400 transition-transform', tzOpen && 'rotate-180')} />
                  </button>
                  {tzOpen && (
                    <div className="absolute right-0 top-full z-50 mt-1 max-h-52 w-56 overflow-y-auto rounded-[3px] border border-slate-200 bg-white py-1 shadow-xl">
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <button
                          key={tz.value}
                          onClick={() => { setTimezone(tz.value); setTzOpen(false) }}
                          className={cn(
                            'flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-[13px] transition-colors',
                            storedTz === tz.value ? 'bg-slate-50 font-semibold text-slate-900' : 'text-slate-600 hover:bg-slate-50',
                          )}
                        >
                          <span className="truncate">{tz.label}</span>
                          <span className="shrink-0 text-[10px] text-slate-400">
                            {timezoneOffsetLabel(tz.value === AUTO_TIMEZONE ? resolvedTz : tz.value)}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="mx-3 my-1.5 border-t border-slate-100" />

                <button
                  onClick={() => { setProfileOpen(false); void handleSignOut() }}
                  className="mx-1 flex w-[calc(100%-8px)] items-center gap-3 rounded-[3px] px-3 py-2.5 text-left text-[13px] text-red-600 transition-colors hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4 text-red-400" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Break Code Selection Dialog */}
      <BreakCodeDialog
        visible={breakDialogOpen}
        onHide={() => setBreakDialogOpen(false)}
      />

      <AiAssistantDrawer open={aiOpen} onClose={() => setAiOpen(false)} />
    </>
  )
}
